import {
  Box,
  Slider,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase } from "../../context/CaseContext";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { CostType } from "../../shared/CaseConfiguration.ts";
import { percentage } from "../../shared/percentage";
import { getColorForCosts } from "../analytics/CostColors.ts";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared.ts";
import { QUIET_SLIDER } from "../general/tableStyles.ts";

export interface CostRecoveryCallbacks {
  onPercentageUpdate(newPercentage: number): void;
  // the share as the slider is dragged, before it is released
  onPercentageSliding?(newPercentage: number): void;
}

/**
 * The recovery of one type of cost as a table row: how much of the cost is
 * likely to be awarded, what the party's costs of that type are and what the
 * two come to. The figures follow the slider while it is dragged; the share
 * is reported when it is released.
 */
export const CostRecoveryDetails = ({
  recovery,
  recoveryCallbacks,
  costsTotal,
}: {
  recovery: {
    type: CostType;
    percentage: number;
  };
  recoveryCallbacks: CostRecoveryCallbacks;
  costsTotal: number;
}) => {
  const [sliderValue, setSliderValue] = useState(recovery.percentage * 100);
  const { readOnly } = useCase();
  const { currency } = useCurrency();
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.recoveries.details",
  });
  const figure = { whiteSpace: "nowrap", ...TABULAR_NUMERALS } as const;

  return (
    <TableRow>
      <TableCell sx={{ py: 1.75 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: 0.5,
              // the outline colour: the type's fill is a wash made for charts
              bgcolor: getColorForCosts(recovery.type, theme).borderColor,
              flex: "none",
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t(`type.${recovery.type}`)}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell sx={{ minWidth: 200 }}>
        <Slider
          sx={{ ...QUIET_SLIDER, display: "block" }}
          color="primary"
          size="small"
          value={sliderValue}
          disabled={readOnly}
          aria-label={t(`description.${recovery.type}`)}
          valueLabelFormat={(value) => percentage.format(value / 100)}
          valueLabelDisplay="off"
          onChangeCommitted={(_, value) => {
            if (typeof value !== "number") return;
            setSliderValue(value);
            recoveryCallbacks.onPercentageUpdate(value / 100);
          }}
          onChange={(_, value) => {
            if (typeof value !== "number") return;
            setSliderValue(value);
            recoveryCallbacks.onPercentageSliding?.(value / 100);
          }}
        />
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, ...TABULAR_NUMERALS }}
        >
          {percentage.format(sliderValue / 100)}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {t("percentage")}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={figure}>
        {currency.format(costsTotal)}
      </TableCell>
      <TableCell align="right" sx={{ ...figure, fontWeight: 700 }}>
        {currency.format((costsTotal * sliderValue) / 100)}
      </TableCell>
    </TableRow>
  );
};
